import { Injectable } from '@angular/core';
import { ActionSheetController, NavController, ToastController } from '@ionic/angular';
import { AccountService } from './account.service';
import { KilnService } from './kiln.service';
import { UserService } from './user.service';

export type AccountAdminQueryParams = Record<string, string>;

export interface AdminLoginAsOption {
  user: { email?: string; username?: string; first_name?: string; last_name?: string };
  account_id: number;
  account_name?: string;
}

@Injectable({ providedIn: 'root' })
export class AdminLoginAsService {
  constructor(
    private userService: UserService,
    private kilnService: KilnService,
    private accountService: AccountService,
    private navCtrl: NavController,
    private toastController: ToastController,
    private actionSheetController: ActionSheetController,
  ) {}

  /** Resolve account(s) for email and start login-as (ActionSheet when multiple accounts). */
  async loginAsByEmail(email: string): Promise<void> {
    const trimmed = (email || '').trim();
    if (!trimmed) {
      await this.showToast('Please provide an email address.');
      return;
    }

    let options: AdminLoginAsOption[] = [];
    try {
      const companies = await this.userService.getUsersAsAdmin(trimmed);
      options = this.collectLoginAsOptions(companies || [], trimmed);
    } catch {
      await this.showToast('There was an error looking up the user, please try again.');
      return;
    }

    if (options.length === 0) {
      await this.executeLoginAs({ email: trimmed, username: trimmed }, 0);
      return;
    }
    if (options.length === 1) {
      await this.executeLoginAs(options[0].user, options[0].account_id);
      return;
    }

    const sheet = await this.actionSheetController.create({
      header: 'Choose account',
      buttons: [
        ...options.map((opt) => ({
          text: opt.account_name || `Account #${opt.account_id}`,
          handler: () => {
            void this.executeLoginAs(opt.user, opt.account_id);
          },
        })),
        { text: 'Cancel', role: 'cancel' },
      ],
    });
    await sheet.present();
  }

  private collectLoginAsOptions(companies: any[], email: string): AdminLoginAsOption[] {
    const normalized = email.toLowerCase();
    const options: AdminLoginAsOption[] = [];

    companies.forEach((company) => {
      const accountId = company?.account_id;
      if (accountId == null || accountId === '') {
        return;
      }
      (company.users || []).forEach((user) => {
        const userEmail = (user?.email || user?.username || '').toLowerCase();
        if (userEmail && userEmail.includes(normalized)) {
          options.push({
            user,
            account_id: Number(accountId),
            account_name: company.name,
          });
        }
      });
    });

    return options;
  }

  async executeLoginAs(
    user: { email?: string; username?: string },
    account_id: number,
    options?: { navigateToKilnList?: boolean },
  ): Promise<any> {
    const navigateToKilnList = options?.navigateToKilnList !== false;
    try {
      const data = await this.userService.setLoginAs(user, account_id);
      this.kilnService.logInAs();
      this.accountService.setCurrent(data.account);
      await this.showToast(`You are now logged in as ${data.email || data.username}`);
      if (navigateToKilnList) {
        await this.navCtrl.navigateRoot('kiln-list');
      }
      return data;
    } catch (error) {
      console.log(error);
      await this.showToast('There was an error logging in as the user, please try again.');
      return null;
    }
  }

  /**
   * Log in as the account owner (or matching user) and open Account Admin — e.g. to add a user.
   */
  async loginAsAndOpenAccountAdmin(
    email: string,
    accountId: number,
    queryParams: AccountAdminQueryParams = { tab: 'users', add: 'user' },
  ): Promise<boolean> {
    const trimmed = (email || '').trim();
    if (!trimmed || accountId == null) {
      await this.showToast('Account owner email is required to add a user.');
      return false;
    }

    let options: AdminLoginAsOption[] = [];
    try {
      const companies = await this.userService.getUsersAsAdmin(trimmed);
      options = this.collectLoginAsOptions(companies || [], trimmed).filter(
        (o) => o.account_id === Number(accountId),
      );
    } catch {
      await this.showToast('There was an error looking up the account owner.');
      return false;
    }

    const pick = options[0];
    const user = pick?.user ?? { email: trimmed, username: trimmed };
    const account_id = pick?.account_id ?? accountId;

    const data = await this.executeLoginAs(user, account_id, { navigateToKilnList: false });
    if (!data) {
      return false;
    }

    try {
      await this.accountService.view(accountId);
    } catch {
      // view may fail for some accounts; still try account admin
    }

    await this.navCtrl.navigateForward(['/account-admin'], { queryParams });
    return true;
  }

  private async showToast(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      position: 'middle',
    });
    await toast.present();
  }
}
